import { useState, useCallback, useEffect } from "react";
import { Users, Plus, Trash2, Pencil, ChevronDown, ChevronRight, FileText, Eye, ArrowUp, ArrowDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BRAND } from "@/lib/constants";
import DocumentDetailDialog from "@/components/documents/DocumentDetailDialog";
import { useConfirm } from "@/hooks/useConfirm";

// ---------------------------------------------------------------------------
// Create Group Dialog
// ---------------------------------------------------------------------------
function CreateGroupDialog({ open, onClose, onCreated, preselectedMembers, allMembers }) {
  const [name, setName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setSelectedMembers(preselectedMembers || []);
    }
  }, [open, preselectedMembers]);

  const toggleMember = (m) => {
    setSelectedMembers((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/groups", { name: name.trim(), members: selectedMembers });
      toast.success("Group created");
      onCreated();
      onClose();
    } catch {
      toast.error("Failed to create group");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold">Create Group</h2>

          <div>
            <label htmlFor="group-name" className="block text-sm font-medium text-gray-700 mb-1">
              Group Name
            </label>
            <input
              id="group-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="e.g. YAD Team"
            />
          </div>

          {allMembers.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Members ({selectedMembers.length} selected)
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-2">
                {allMembers.map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(m)}
                      onChange={() => toggleMember(m)}
                      aria-label={m}
                    />
                    {m}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!name.trim() || saving}
              style={{ backgroundColor: BRAND.bg }}
              className="text-white hover:opacity-90"
            >
              {saving ? "Saving..." : "Create Group"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Group Dialog
// ---------------------------------------------------------------------------
function EditGroupDialog({ open, group, onClose, onUpdated, allMembers }) {
  const [name, setName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && group) {
      setName(group.name);
      setSelectedMembers([...group.members]);
    }
  }, [open, group]);

  const toggleMember = (m) => {
    setSelectedMembers((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    );
  };

  const combinedMembers = [...new Set([...allMembers, ...(group?.members || [])])].sort();

  const handleSave = async () => {
    if (!name.trim() || !group) return;
    setSaving(true);
    try {
      await api.patch(`/groups/${group.group_id}`, {
        name: name.trim(),
        members: selectedMembers,
      });
      toast.success("Group updated");
      onUpdated();
      onClose();
    } catch {
      toast.error("Failed to update group");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !group) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold">Edit Group</h2>

          <div>
            <label htmlFor="edit-group-name" className="block text-sm font-medium text-gray-700 mb-1">
              Group Name
            </label>
            <input
              id="edit-group-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {combinedMembers.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Members ({selectedMembers.length} selected)
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-2">
                {combinedMembers.map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(m)}
                      onChange={() => toggleMember(m)}
                      aria-label={m}
                    />
                    {m}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!name.trim() || saving}
              style={{ backgroundColor: BRAND.bg }}
              className="text-white hover:opacity-90"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add to Group Dropdown
// ---------------------------------------------------------------------------
function AddToGroupDropdown({ groups, selectedMembers, onCreated, allMembers }) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [adding, setAdding] = useState(null);

  const handleAddToExisting = async (group) => {
    setAdding(group.group_id);
    try {
      const merged = [...new Set([...group.members, ...selectedMembers])];
      await api.patch(`/groups/${group.group_id}`, { members: merged });
      toast.success(`Added ${selectedMembers.length} member(s) to "${group.name}"`);
      onCreated();
      setOpen(false);
    } catch {
      toast.error("Failed to add members to group");
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="relative">
      <Button
        size="sm"
        onClick={() => setOpen(!open)}
        style={{ backgroundColor: BRAND.bg }}
        className="text-white hover:opacity-90"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add to Group ({selectedMembers.length})
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-40 w-56">
          <div className="p-2 border-b">
            <button
              onClick={() => { setCreateOpen(true); setOpen(false); }}
              className="w-full text-left text-sm px-3 py-2 rounded hover:bg-gray-50 flex items-center gap-2 font-medium"
              style={{ color: BRAND.text }}
            >
              <Plus className="w-4 h-4" />
              Create New Group
            </button>
          </div>
          {groups.length > 0 && (
            <div className="max-h-48 overflow-y-auto p-1">
              {groups.map((g) => (
                <button
                  key={g.group_id}
                  onClick={() => handleAddToExisting(g)}
                  disabled={adding === g.group_id}
                  className="w-full text-left text-sm px-3 py-2 rounded hover:bg-gray-50 flex items-center gap-2"
                >
                  <Users className="w-3 h-3 text-gray-400" />
                  <span className="truncate">{g.name}</span>
                  <span className="text-[10px] text-gray-400 ml-auto">{g.members.length}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <CreateGroupDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={onCreated}
        preselectedMembers={selectedMembers}
        allMembers={allMembers}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member Row — with doc count and source PDF viewer
// ---------------------------------------------------------------------------
function MemberRow({ name, checked, onToggle, onViewSource, groupNames }) {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-2.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select ${name}`}
          className="rounded"
        />
      </td>
      <td className="px-4 py-2.5">
        <p className="text-sm text-gray-900 font-medium">{name}</p>
        {groupNames.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {groupNames.map((g) => (
              <span
                key={g}
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ backgroundColor: BRAND.soft, color: BRAND.text }}
              >
                {g}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={onViewSource}
          className="h-7 text-[10px] gap-1"
          style={{ color: BRAND.text }}
        >
          <Eye className="w-3 h-3" />
          View Source
        </Button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Group Card
// ---------------------------------------------------------------------------
function GroupCard({ group, onEdit, onDelete, expanded, onToggle }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 cursor-pointer"
        onClick={onToggle}
      >
        <button className="text-gray-400">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{group.name}</p>
          <p className="text-[10px] text-gray-400">{group.members.length} members</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            title="Edit group"
            className="text-gray-400 hover:text-blue-500 p-1"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Delete group"
            className="text-gray-400 hover:text-red-500 p-1"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {expanded && group.members.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex flex-wrap gap-1">
            {group.members.map((m) => (
              <Badge key={m} variant="secondary" className="text-xs">
                {m}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function FundsPage() {
  const [groups, setGroups] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  // Selection
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  // PDF viewer
  const [viewingDoc, setViewingDoc] = useState(null);

  // Search + Sort
  const [memberSearch, setMemberSearch] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // asc or desc

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Use /documents/stats for member names (fast distinct() query)
      // instead of /groups/discovered (reads ALL docs — very slow)
      const [groupsRes, statsRes] = await Promise.all([
        api.get("/groups"),
        api.get("/documents/stats"),
      ]);
      const fetchedGroups = groupsRes.data?.groups || [];
      // Alphabetical sort, case-insensitive, trim — matches the
      // DocumentsPage groups dropdown (DocumentsPage.jsx:213).
      fetchedGroups.sort((a, b) =>
        (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
      setGroups(fetchedGroups);
      setAllMembers(statsRes.data?.member_names || []);
    } catch {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  // Build member → group name mapping
  const memberGroupMap = {};
  groups.forEach((g) => {
    g.members.forEach((m) => {
      if (!memberGroupMap[m]) memberGroupMap[m] = [];
      memberGroupMap[m].push(g.name);
    });
  });

  // Filtered + sorted members list
  const filteredMembers = (memberSearch
    ? allMembers.filter((m) => m.toLowerCase().includes(memberSearch.toLowerCase()))
    : [...allMembers]
  ).sort((a, b) => sortDir === "asc" ? a.localeCompare(b) : b.localeCompare(a));

  // Select all / deselect all
  const allSelected = filteredMembers.length > 0 && filteredMembers.every((m) => selectedMembers.includes(m));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedMembers((prev) => prev.filter((m) => !filteredMembers.includes(m)));
    } else {
      setSelectedMembers((prev) => [...new Set([...prev, ...filteredMembers])]);
    }
  };

  const toggleMember = (member) => {
    setSelectedMembers((prev) =>
      prev.includes(member) ? prev.filter((m) => m !== member) : [...prev, member]
    );
  };

  const handleDelete = async (groupId, groupName) => {
    const ok = await confirm({
      title: `Delete group "${groupName}"?`,
      description: "This won't affect your documents.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/groups/${groupId}`);
      toast.success("Group deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete group");
    }
  };

  const handleViewSource = async (memberName) => {
    try {
      const res = await api.get(`/documents?member_name=${encodeURIComponent(memberName)}&per_page=1&sort=date&order=desc`);
      const docs = res.data?.documents || [];
      if (docs.length > 0) {
        setViewingDoc(docs[0]);
      } else {
        toast.info(`No documents found for "${memberName}"`);
      }
    } catch {
      toast.error("Failed to load source document");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Groups</h1>
          <p className="text-sm text-gray-500 mt-1">Loading...</p>
        </div>
        <Card>
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-16 ml-auto" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Groups</h1>
          <p className="text-sm text-gray-500 mt-1">
            Organize members into groups. Select members below and add them to a group.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          style={{ backgroundColor: BRAND.bg }}
          className="text-white hover:opacity-90"
        >
          <Plus className="w-4 h-4 mr-1" />
          Create Group
        </Button>
      </div>

      {/* Existing Groups */}
      {groups.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Your Groups ({groups.length})
          </h2>
          <div className="space-y-2">
            {groups.map((g) => (
              <GroupCard
                key={g.group_id}
                group={g}
                expanded={expandedGroupId === g.group_id}
                onToggle={() => setExpandedGroupId((prev) => prev === g.group_id ? null : g.group_id)}
                onEdit={() => setEditGroup(g)}
                onDelete={() => handleDelete(g.group_id, g.name)}
              />
            ))}
          </div>
        </div>
      )}

      {/* All Members section */}
      <Card>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              All Members ({allMembers.length})
            </h2>
            <p className="text-[10px] text-gray-400">
              Check members and use "Add to Group" to organize them.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Search */}
            <input
              type="text"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder="Search members..."
              className="border border-gray-300 rounded-md px-3 py-1.5 text-xs w-48"
            />
            {/* Add to Group button — visible when members selected */}
            {selectedMembers.length > 0 && (
              <AddToGroupDropdown
                groups={groups}
                selectedMembers={selectedMembers}
                onCreated={() => { fetchData(); setSelectedMembers([]); }}
                allMembers={allMembers}
              />
            )}
          </div>
        </div>

        <CardContent className="p-0">
          {filteredMembers.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                {memberSearch ? "No members match your search." : "No members discovered yet. Members appear after connections run."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all members"
                        className="rounded"
                      />
                    </th>
                    <th
                      className="px-4 py-2 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 select-none"
                      onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}
                    >
                      <span className="inline-flex items-center gap-1">
                        Member Name
                        {sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      </span>
                    </th>
                    <th className="px-4 py-2 text-right text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((m) => (
                    <MemberRow
                      key={m}
                      name={m}
                      checked={selectedMembers.includes(m)}
                      onToggle={() => toggleMember(m)}
                      onViewSource={() => handleViewSource(m)}
                      groupNames={memberGroupMap[m] || []}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <CreateGroupDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { fetchData(); setSelectedMembers([]); }}
        preselectedMembers={selectedMembers}
        allMembers={allMembers}
      />

      {/* Edit Dialog */}
      <EditGroupDialog
        open={!!editGroup}
        group={editGroup}
        onClose={() => setEditGroup(null)}
        onUpdated={fetchData}
        allMembers={allMembers}
      />

      {/* PDF Viewer Dialog — reuses the existing DocumentDetailDialog */}
      <DocumentDetailDialog
        doc={viewingDoc}
        open={!!viewingDoc}
        onOpenChange={(open) => { if (!open) setViewingDoc(null); }}
      />
    </div>
  );
}
